import React, { useContext, useEffect, useState } from 'react';
import Switch, { SwitchClassKey, SwitchProps } from '@material-ui/core/Switch';
import { withStyles } from '@material-ui/core/styles';
import contextos from '../../config/contextos';
import { useConfirmDialogContext } from '../../contexts/ConfirmDialogContext';
import LoadingContext from '../../contexts/LoadingContext';
import { useToast } from '../../contexts/ToastContext';
import api from '../../service/api';

import './styles.css';
import { useAuth } from '../../contexts/AuthContext';

interface SwitchStyles extends Partial<Record<SwitchClassKey, string>> {
  focusVisible?: string;
}

interface Props extends SwitchProps {
  classes: SwitchStyles;
}

const PurpleSwitch = withStyles({
  switchBase: {
    '&$checked': {
      color: '#009688',
    },
    '&$checked + $track': {
      backgroundColor: '#009688',
    },
  },
  checked: {},
  track: {},
})(Switch);

const Card: React.FC<any> = ({ imobiliaria, ambiente }) => {
  const { jwt, signOut } = useAuth();
  api.interceptors.response.use(function (response) {
    return response;
  }, function (error) {
    if(error.message === 'Request failed with status code 401'){
      signOut();
    }else{
      return error;
    }
  });

  if(jwt) api.defaults.headers = { 'Authorization': `Bearer ${jwt}` }

  const { changeLoadingState } = useContext(LoadingContext);
  const { openConfirmation, setDialogMessages } = useConfirmDialogContext();
  const { addToast } = useToast();
  const [aprovarDispositivo, setAprovarDispositivo] = useState<boolean>(false);

  useEffect(() => {
    setAprovarDispositivo(imobiliaria.aprovarDispositivo);
  },[imobiliaria.aprovarDispositivo])

  async function onButtonClick(
    env: string,
    contexto: string,
    acao: string,
    tenantId: string,
    temBackup: boolean = false
  ) {
    setDialogMessages({
      title: `Confirmação de ${acao === 'reiniciar' ? 'reinicialização' : 'atualização'}`,
      description: `Tem certeza que deseja ${acao} o banco ${contexto} da imobiliária ${imobiliaria.codigo}?`,      
    });
    const dialogResult = await openConfirmation();
    if (!dialogResult) return;
    if (temBackup) {
      setDialogMessages({
        title: 'Arquivo de backup existente',
        description: 'Já existe um arquivo de backup para essa imobiliária. Deseja sobrescrevê-lo?',
        warning: 'Caso o banco esteja vazio, o arquivo de backup será substituído por um backup vazio'
      });
      const dialogResult = await openConfirmation();
      if (!dialogResult) return;
    }
    const rContexto = (contextos as any)[contexto];
    const rEnv = rContexto[env];
    const url = rEnv[acao];
    try {
      changeLoadingState(true);
      await api.put('/acaoImobiliaria', {
        urlAcao: url + tenantId,
        env,
        acao,
        contexto,
      });
      changeLoadingState(false);
      addToast({
        type: 'success',
        title: `Ambiente ${acao === 'reiniciar' ? 'reiniciado' : 'atualizado'}`,
        description: `O ambiente foi  ${
          acao === 'reiniciar' ? 'reiniciado' : 'atualizado'
        } com sucesso`,
      });
    } catch (e) {
      console.log(e);
      changeLoadingState(false);
      addToast({
        type: 'error',
        title: `Erro ao ${acao}`,
        description: `Ocorreu um erro ao ${acao} o ambiente. Tente novamente`,
      });
    }
  }

  const handleAprovarSwitchChange = async () => {
    try{
      setDialogMessages({
        title: `Confirmação`,
        description: `Tem certeza que deseja ${aprovarDispositivo ? 'desabilitar' : 'habilitar'} a configuração de "apenas dispositivos autorizados" da imobiliária ${imobiliaria.codigo}?`,
        warning: `Ao desabilitar essa configuração, os dispositivos que estão com a situação 'Aguardando aprovação' serão removidos do banco.`
      });
      const dialogResult = await openConfirmation();
      if (!dialogResult) return;
      changeLoadingState(true);
      const resposta = await api.get(`/dados-seguranca/${ambiente}/${imobiliaria.tenantId}`);
      if(!resposta || !resposta.data){
        throw new Error();
      }
      const payload = {...resposta.data, aprovarDispositivo: !aprovarDispositivo, tenantId: imobiliaria.tenantId};
      await api.post(`/dados-seguranca/${ambiente}`,payload);
      if(aprovarDispositivo){
        await api.delete(`/excluir-aguardando-aprovacao/${ambiente}/${imobiliaria.tenantId}`);
      }
      setAprovarDispositivo((state) => !state);
      changeLoadingState(false);
      addToast({
        type: 'success',
        title: `Configurações atualizadas`,
        description: `As configurações de dispositivos foram alteradas com sucesso`,
      });
    }catch(e){
      console.log(e);
      changeLoadingState(false);
      addToast({
        type: 'error',
        title: `Ocorreu um erro`,
        description: `Ocorreu um erro ao alterar a configuração de dispositivos. Tente novamente`,
      });
    }
  };


  return (
    <div key={imobiliaria.tenantId} className='card-imob'>
      <p className='codigo'>Código: {imobiliaria.codigo}</p>
      <p>Razão Social: {imobiliaria.razaoSocial}</p>
      <p>Nome Fantasia: {imobiliaria.nomeFantasia}</p>
      <p className='acoes'>Ações</p>
      <div className='container-ambiente'>
        <span className="container-ambiente-span">{ambiente === 'PROD' ? 'Produção' : 'Ambiente de teste'}</span>
        {Object.keys(contextos).map((contexto, idx) => {
          return (
            <div key={idx}>
              <p>{contexto}</p>
              <hr></hr>
              <div className='container-buttons'>
                <button
                  type='button'
                  onClick={() =>
                    onButtonClick(
                      ambiente,
                      contexto,
                      'reiniciar',
                      imobiliaria.tenantId,
                      imobiliaria.temBackup && true
                    )
                  }
                >
                  Reiniciar
                </button>
                <button
                  type='button'
                  onClick={() =>
                    onButtonClick(
                      ambiente,
                      contexto,
                      'atualizar',
                      imobiliaria.tenantId
                    )
                  }
                >
                  Atualizar
                </button>
              </div>
            </div>
          );
        })}
        <div>
          <p>Identidade e Acesso</p>
          <hr></hr>
          <div className='container-buttons'>
            <span style={{fontSize: '14px'}}>Apenas dispositivos aprovados:</span>
            <PurpleSwitch 
              checked={aprovarDispositivo}
              onChange={handleAprovarSwitchChange}
              name="aprovarDispositivo"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Card;
